import React, { useState, useMemo, useEffect } from 'react';
import { X, Users, Briefcase, Clock, MapPin, TrendingUp, Filter, Download, ChevronDown, Check } from 'lucide-react';
import * as XLSX from 'xlsx';
import { api } from '../utils/api';
import { useModalA11y } from '../hooks/useModalA11y';

export function PollInsightsModal({ poll, onClose }) {
    const [rawVotes, setRawVotes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const containerRef = useModalA11y(true, onClose);

    // Filter State
    const [filters, setFilters] = useState({
        age: [],
        gender: [],
        region: [],
        workStatus: []
    });

    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // CONSTANTS based on actual system traits
    const AGES = ['18-24', '25-34', '35-44', '45-54', '55+'];
    const GENDERS = ['Male', 'Female', 'Non-Binary', 'Other'];
    const REGIONS = ['North America', 'Europe', 'Asia', 'South America', 'Africa', 'Oceania', 'Unknown'];
    const WORK_STATUS = ['Working', 'Student', 'Retired', 'Homemaker', 'Skipped', 'Unknown'];

    // 1. FETCH ACTUAL DATA
    useEffect(() => {
        let isMounted = true;
        const fetchInsights = async () => {
            try {
                setIsLoading(true);
                const data = await api.getInsights(poll.id);
                if (isMounted) {
                    setRawVotes(data || []);
                }
            } catch (err) {
                console.error("[PollInsightsModal] Failed to fetch insights:", err);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };
        fetchInsights();
        return () => { isMounted = false; };
    }, [poll.id]);

    // 2. FILTER ENGINE
    const filteredVotes = useMemo(() => {
        return rawVotes.filter(v => {
            if (filters.age.length > 0 && !filters.age.includes(v.age)) return false;
            if (filters.gender.length > 0 && !filters.gender.includes(v.gender)) return false;
            if (filters.region.length > 0 && !filters.region.includes(v.region)) return false;
            if (filters.workStatus.length > 0 && !filters.workStatus.includes(v.workStatus)) return false;
            return true;
        });
    }, [rawVotes, filters]);

    // 3. AGGREGATION ENGINE
    const stats = useMemo(() => {
        if (filteredVotes.length === 0) return null;

        // Find the global leading option across the filtered set
        const optionCounts = {};
        let totalWeight = 0;
        filteredVotes.forEach(v => {
            const w = v.weight || 1;
            optionCounts[v.vote] = (optionCounts[v.vote] || 0) + w;
            totalWeight += w;
        });

        let topOptionIndex = '0';
        let maxWeight = 0;
        Object.entries(optionCounts).forEach(([choiceId, weight]) => {
            if (weight > maxWeight) {
                maxWeight = weight;
                topOptionIndex = choiceId;
            }
        });

        const topOptionName = poll.options[parseInt(topOptionIndex)] || 'Leading Option';
        const topOptionPct = totalWeight > 0 ? Math.round((maxWeight / totalWeight) * 100) : 0;

        const calculateStats = (key) => {
            const groupCounts = {};
            filteredVotes.forEach(v => {
                const val = v[key] || 'Unknown';
                if (!groupCounts[val]) groupCounts[val] = { topOptionWeight: 0, totalWeight: 0, totalPeople: 0 };
                
                const w = v.weight || 1;
                groupCounts[val].totalWeight += w;
                groupCounts[val].totalPeople++;
                
                if (v.vote == topOptionIndex) {
                    groupCounts[val].topOptionWeight += w;
                }
            });

            return Object.entries(groupCounts).map(([name, data]) => ({
                name,
                topOptionPct: data.totalWeight > 0 ? Math.round((data.topOptionWeight / data.totalWeight) * 100) : 0,
                total: data.totalPeople
            })).sort((a, b) => b.total - a.total);
        };

        return {
            age: calculateStats('age'),
            gender: calculateStats('gender'),
            region: calculateStats('region'),
            workStatus: calculateStats('workStatus'),
            totalVotes: filteredVotes.length,
            topOptionName,
            topOptionPct
        };
    }, [filteredVotes, poll.options]);

    // HANDLERS
    const toggleFilter = (category, value) => {
        setFilters(prev => {
            const current = prev[category];
            const updated = current.includes(value)
                ? current.filter(item => item !== value)
                : [...current, value];
            return { ...prev, [category]: updated };
        });
    };

    const handleExport = () => {
        setIsExporting(true);
        setTimeout(() => {
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(filteredVotes);
            XLSX.utils.book_append_sheet(wb, ws, "Filtered_Votes");
            XLSX.writeFile(wb, `Poll_Insights_${poll.id}_${new Date().toISOString().slice(0, 10)}.xlsx`);
            setIsExporting(false);
        }, 1000);
    };

    // UI COMPONENTS
    const renderFilterGroup = (label, category, options) => (
        <div className="mb-4">
            <h4 className="text-2xs font-black text-gray-500 uppercase tracking-widest mb-2">{label}</h4>
            <div className="flex flex-wrap gap-2">
                {options.map(opt => (
                    <button
                        key={opt}
                        onClick={() => toggleFilter(category, opt)}
                        className={`px-3 py-1.5 rounded-lg text-2xs font-bold uppercase tracking-wide border transition-all ${filters[category].includes(opt)
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                    >
                        {opt}
                    </button>
                ))}
            </div>
        </div>
    );

    const renderTable = (data, title, icon, topName) => (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden mb-6 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-50 dark:border-gray-700 flex items-center justify-between bg-gray-50/50 dark:bg-gray-700/30">
                <div className="flex items-center space-x-2">
                    {icon}
                    <h3 className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-widest">{title}</h3>
                </div>
                <span className="text-2xs font-bold text-gray-500">{data.length} Groups</span>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full min-w-[420px]">
                <thead className="bg-gray-50 dark:bg-gray-700/30">
                    <tr>
                        <th className="px-6 py-3 text-left text-2xs font-black text-gray-500 uppercase tracking-widest">Group</th>
                        <th className="px-6 py-3 text-left text-2xs font-black text-gray-500 uppercase tracking-widest">Results</th>
                        <th className="px-6 py-3 text-right text-2xs font-black text-gray-500 uppercase tracking-widest">Votes</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                    {data.map((row) => (
                        <tr key={row.name} className="hover:bg-gray-50/80 dark:hover:bg-gray-700/50 transition-colors group">
                            <td className="px-6 py-4 text-sm font-bold text-gray-900 dark:text-white">{row.name}</td>
                            <td className="px-6 py-4">
                                <div className="flex items-center space-x-3">
                                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden w-24">
                                        <div className="h-full bg-primary-600" style={{ width: `${row.topOptionPct}%` }}></div>
                                    </div>
                                    <span className="text-xs font-black text-primary-600 max-w-[100px] truncate" title={`${row.topOptionPct}% for ${topName}`}>
                                        {row.topOptionPct}% {topName}
                                    </span>
                                </div>
                            </td>
                            <td className="px-6 py-4 text-right text-xs font-mono text-gray-500">{row.total.toLocaleString()}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 z-[250] flex items-center justify-center p-2 sm:p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-md animate-in fade-in duration-300" onClick={onClose}></div>
            <div ref={containerRef} role="dialog" aria-modal="true" aria-label={`Insights for: ${poll.question}`} className="relative w-full max-w-6xl h-[90vh] bg-white dark:bg-gray-900 rounded-modal shadow-2xl animate-in zoom-in-95 duration-300 border border-gray-100 dark:border-gray-800 flex flex-col overflow-hidden">

                {/* HEADER */}
                <div className="px-8 py-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-white dark:bg-gray-900">
                    <div>
                        <div className="flex items-center space-x-2 mb-1">
                            <span className="px-2 py-0.5 bg-primary-600/10 text-primary-600 rounded-md text-2xs font-black uppercase tracking-widest">Verified Result</span>
                            <span className="text-2xs font-bold text-gray-500 uppercase tracking-widest">ID: {poll.id.split('_')[1]}</span>
                        </div>
                        <h2 className="text-3xl font-black italic uppercase tracking-tighter text-gray-900 dark:text-white leading-tight max-w-2xl truncate">{poll.question}</h2>
                    </div>
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={handleExport}
                            disabled={isExporting || isLoading || rawVotes.length === 0}
                            className="flex items-center space-x-2 px-5 py-2.5 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-xl hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors border border-green-200 dark:border-green-800 disabled:opacity-50"
                        >
                            {isExporting ? <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div> : <Download size={18} />}
                            <span className="text-xs font-black uppercase tracking-wide">{isExporting ? 'Exporting...' : 'Export CSV'}</span>
                        </button>
                        <button onClick={onClose} aria-label="Close insights" className="p-2.5 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-all text-gray-500 hover:text-gray-900 dark:hover:text-white">
                            <X size={24} />
                        </button>
                    </div>
                </div>

                <div className="flex flex-1 overflow-hidden">
                    {/* SIDEBAR: FILTER LAB */}
                    <div className={`w-80 bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800 flex flex-col transition-all duration-300 ${isFilterOpen ? 'translate-x-0' : '-ml-80'} md:ml-0`}>
                        <div className="p-6 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
                            <h3 className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-[0.2em] flex items-center">
                                <Filter size={14} className="mr-2 text-primary-600" />
                                Filters
                            </h3>
                        </div>
                        <div className="p-6 overflow-y-auto flex-1 space-y-6">
                            {renderFilterGroup('Ages', 'age', AGES)}
                            {renderFilterGroup('Gender', 'gender', GENDERS)}
                            {renderFilterGroup('Regions', 'region', REGIONS)}
                            {renderFilterGroup('Work Status', 'workStatus', WORK_STATUS)}

                            <div className="pt-6 border-t border-gray-100 dark:border-gray-800">
                                <div className="p-4 bg-primary-600/5 rounded-xl border border-primary-600/20">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-2xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">People Voting</span>
                                        <span className="text-sm font-black text-primary-600 tabular-nums">{filteredVotes.length}</span>
                                    </div>
                                    <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                        <div className="h-full bg-primary-600 transition-all duration-500" style={{ width: rawVotes.length ? `${(filteredVotes.length / rawVotes.length) * 100}%` : '0%' }}></div>
                                    </div>
                                    <div className="mt-2 text-2xs text-gray-500 font-bold text-center">
                                        Showing {rawVotes.length ? Math.round((filteredVotes.length / rawVotes.length) * 100) : 0}% of Total
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* MAIN CONTENT: CHARTS */}
                    <div className="flex-1 overflow-y-auto bg-[#FDFCFE] dark:bg-gray-900 p-8">
                        {/* TOGGLE FOR MOBILE FILTERS */}
                        <button
                            className="md:hidden mb-6 flex items-center space-x-2 text-xs font-black uppercase tracking-widest text-primary-600"
                            onClick={() => setIsFilterOpen(!isFilterOpen)}
                        >
                            <Filter size={16} />
                            <span>{isFilterOpen ? 'Hide Filters' : 'Show Filters'}</span>
                        </button>

                        {isLoading ? (
                            <div className="flex flex-col items-center justify-center h-full space-y-4">
                                <div className="w-12 h-12 border-4 border-primary-600/20 border-t-primary-600 rounded-full animate-spin"></div>
                                <p className="text-xs font-black uppercase tracking-widest text-gray-500">Loading Global Consensus...</p>
                            </div>
                        ) : !stats ? (
                            <div className="flex flex-col items-center justify-center h-full space-y-4">
                                <Users size={48} className="text-gray-300 dark:text-gray-700" />
                                <p className="text-sm font-black uppercase tracking-widest text-gray-500">No votes found for these filters.</p>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                                    {/* KEY METRIC CARD */}
                                    <div className="p-6 bg-white dark:bg-gray-800 rounded-[24px] border border-gray-100 dark:border-gray-700 shadow-xl shadow-purple-900/5 relative overflow-hidden group">
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-primary-600/10 rounded-full blur-3xl -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-1000"></div>
                                        <h4 className="text-2xs font-black text-gray-500 dark:text-gray-500 uppercase tracking-[0.2em] mb-4 relative z-10">Leading Option</h4>
                                        <div className="flex flex-col items-start relative z-10">
                                            <span className="text-6xl font-black italic tracking-tighter text-primary-600 dark:text-white leading-none mb-2">{stats.topOptionPct}%</span>
                                            <span className="text-xl font-bold text-gray-900 dark:text-purple-200 uppercase truncate max-w-full">{stats.topOptionName}</span>
                                        </div>
                                        <div className="mt-4 flex items-center space-x-2 text-2xs font-bold text-gray-500 dark:text-gray-500 relative z-10">
                                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                                            <span>Based on {filteredVotes.length} verified voters</span>
                                        </div>
                                    </div>

                                    {/* CURIOSITY INSIGHT CARD */}
                                    <div className="p-6 bg-white dark:bg-gray-800 rounded-[24px] border border-gray-100 dark:border-gray-700 shadow-lg relative overflow-hidden">
                                        <div className="flex items-center space-x-3 mb-4">
                                            <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center text-amber-600 dark:text-amber-500">
                                                <TrendingUp size={18} />
                                            </div>
                                            <h4 className="text-2xs font-black text-gray-500 uppercase tracking-[0.2em]">Quick Take</h4>
                                        </div>
                                        <p className="text-lg font-bold text-gray-800 dark:text-gray-200 italic leading-relaxed">
                                            "When filtering for <span className="text-primary-600">{filters.age.join(', ') || 'All Ages'}</span> in <span className="text-secondary-600">{filters.region.join(', ') || 'Global'}</span>, <span className="text-gray-900 dark:text-white font-black">{stats.topOptionName}</span> is the dominant choice with <span className="text-green-600">{stats.topOptionPct}%</span> of the weighted consensus."
                                        </p>
                                    </div>
                                </div>

                                {/* DATA TABLES */}
                                <div className="space-y-2">
                                    {renderTable(stats.age, 'Results by Age', <Clock size={16} className="text-primary-600" />, stats.topOptionName)}
                                    {renderTable(stats.region, 'Results by Region', <MapPin size={16} className="text-secondary-600" />, stats.topOptionName)}
                                    {renderTable(stats.gender, 'Results by Gender', <Users size={16} className="text-blue-500" />, stats.topOptionName)}
                                    {renderTable(stats.workStatus, 'Results by Work Status', <Briefcase size={16} className="text-gray-500" />, stats.topOptionName)}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

